<script setup lang="ts">

import AuthenticatedLayout from "@/layout/AuthenticatedLayout.vue";
import {computed, defineAsyncComponent, onMounted, ref} from "vue";
import yapi from "@/lib/yapi";
import {useApiCall} from "@/composables/useApiCall";
import {Institution} from "@YenzaCT/sdk";
import {useRoutedTabbedDashboard} from "@/composables/useRoutedTabbedDashboard";
import PageHeader from "@/components/PageHeader.vue";
import {useRouter} from "vue-router";
import {useReportingStore} from "@/store/reporting";
import {useGlobalStore} from "@/store";

// LAzy load the DashboardTab component
const DashboardTab = defineAsyncComponent(() => import("./tabs/DashboardTab.vue"));
const InstitutionsTab = defineAsyncComponent(() => import("./tabs/InstitutionsTab.vue"));
const StudentsTab = defineAsyncComponent(() => import("./tabs/StudentsTab.vue"));
const LicensesTab = defineAsyncComponent(() => import("./tabs/LicensesTab.vue"));
const ChoicesTab = defineAsyncComponent(() => import("../ui/ChoicesTab.vue"));
const AssessmentsTab = defineAsyncComponent(() => import("../ui/AssessmentsTab.vue"));

const {executeApi} = useApiCall();
const institutions = ref<Institution[]>([]);
const {activeTab} = useRoutedTabbedDashboard("schoolsDashboardWithoutTenant", null);
const reportingStore = useReportingStore();
const router = useRouter();
const store = useGlobalStore();
const user = store.user;
const initialized = ref(false);

const tabComponents = {
  dashboard: DashboardTab,
  institutions: InstitutionsTab,
  students: StudentsTab,
  licenses: LicensesTab,
  choices: ChoicesTab,
  assessments: AssessmentsTab
};

const currentTabComponent = computed(() =>
  tabComponents[activeTab.value as keyof typeof tabComponents] || DashboardTab);

onMounted(async () => {
  initialized.value = false;
  await setup();
  initialized.value = true;
});

async function setup() {
  await fetchInstitutions();
  reportingStore.setFiltersAndRefresh({
    institutions: store.accessibleInstitutions,
  });
}

async function fetchInstitutions() {
  const result = await executeApi(async () => {
    const res = await yapi.admin.institution.paginate();
    return res.data.docs;
  });
  if (result) {
    institutions.value = result;
  }
}

function handleViewAllUsersClick() {
  router.push({
    name: "users",
  });
}

</script>

<template>
  <authenticated-layout class="" :breadcrumbs="[
    { text: 'My Schools' },
    { text: 'All', to: { name: 'schoolsDashboardWithoutTenant' } },
  ]">

    <page-header title="My Institutions"
                 :description="`Welcome back, ${user?.profile?.firstName}. Here is what is happening across your institutions`">
    </page-header>

    <v-container class="mt-0 mx-auto pa-0 border-b" fluid>
      <v-tabs v-model="activeTab" grow>
        <v-tab value="dashboard">
          Dashboard
        </v-tab>
        <v-tab value="institutions">
          Institutions
        </v-tab>
        <v-tab value="students">
          Students
        </v-tab>
        <v-tab value="licenses">
          Licenses
        </v-tab>
        <v-tab value="choices">
          Choices
        </v-tab>
        <v-tab value="assessments">
          Assessments
        </v-tab>
      </v-tabs>
    </v-container>

    <div>
      <v-container class="mx-auto" fluid>
        <component
          v-if="initialized"
          :is="currentTabComponent"
          :institutions="institutions"
          @view-all-users="handleViewAllUsersClick"
        />
      </v-container>
    </div>

  </authenticated-layout>
</template>

<style scoped>

</style>
